import Matter from 'matter-js'

const EURO_TEXTURE = '/assets/svg/wat-euro-matter.svg'

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
}

function initFalling2DMatterJS(root = document) {
    const canvas = root.querySelector('#canvas-target')

    // Check if canvas is on page
    if (!canvas || canvas.hasAttribute('data-matter-initialized')) {
        return
    }

    canvas.setAttribute('data-matter-initialized', '')

    canvas.querySelectorAll('canvas').forEach((existingCanvas) => {
        existingCanvas.remove()
    })

    const viewportWidth = window.innerWidth
    const isMobile = viewportWidth < 768
    const boundsElement =
        canvas.clientWidth && canvas.clientHeight
            ? canvas
            : canvas.parentElement || canvas

    if (isMobile && boundsElement !== canvas) {
        boundsElement.style.height = '100svh'
        boundsElement.style.aspectRatio = 'auto'
    }

    const canvasWidth = boundsElement.clientWidth + 2
    const canvasHeight = boundsElement.clientHeight + 2
    const canvasWallDepth = canvasWidth / 4
    const smileyAmount = clamp(Math.round(viewportWidth / 100), 8, 22) // Amount of objects added to the canvas
    const smileySize = clamp(viewportWidth * 0.075, 52, 120) // Size of objects
    const smileyStartY = isMobile ? smileySize / 2 : smileySize
    const smileyInitialVelocityY = isMobile ? 28 : 0
    const smileySpawnDelay = 90
    const topWallY = isMobile
        ? canvasWallDepth * -1 - smileySize * 4
        : canvasWallDepth * -1
    const smileySizeTexture = 188 // Size of object texture in pixels
    const smileySizeScale = (smileySize / smileySizeTexture) * 1.3
    const smileyRestitution = 0.75 // Bounciness
    const worldGravity = 2 // Gravity

    // Modules
    let {
        Engine,
        Render,
        Runner,
        Bodies,
        Body,
        Composite,
        Mouse,
        MouseConstraint
    } = Matter

    // Create an engine
    let engine = Engine.create()
    engine.world.gravity.y = worldGravity

    // Create a renderer
    let render = Render.create({
        element: canvas,
        engine: engine,
        options: {
            background: 'transparent',
            wireframes: false,
            width: canvasWidth,
            height: canvasHeight,
            pixelRatio: 2,
            border: 'none'
        }
    })

    // Generate a random number between min (inclusive) and max (exclusive)
    function getRandomNumber(min, max) {
        return Math.random() * (max - min) + min
    }

    let min = smileySize / 2 // min value
    let max = canvasWidth - smileySize / 2 // max value

    // Textures
    let textureArray = [EURO_TEXTURE]

    // Function to loop trough all textures
    let textureIndex = 0
    function getNextTexture() {
        const texture = textureArray[textureIndex]
        textureIndex = (textureIndex + 1) % textureArray.length
        return texture
    }

    // Function to get a random texture
    function getRandomTexture() {
        const randomIndex = Math.floor(Math.random() * textureArray.length)
        return textureArray[randomIndex]
    }

    // Create smiley
    const smileyCreate = (isFirst = false) => {
        let smiley = Bodies.rectangle(
            getRandomNumber(min, max),
            isFirst ? smileySize / 2 : smileyStartY,
            smileySize,
            smileySize,
            {
                chamfer: {
                    radius: smileySize / 2
                },
                restitution: smileyRestitution,
                render: {
                    sprite: {
                        texture: getNextTexture(), // Replace with "getRandomTexture()" to get random textures
                        xScale: smileySizeScale,
                        yScale: smileySizeScale
                    }
                }
            }
        )
        if (smileyInitialVelocityY || isFirst) {
            Body.setVelocity(smiley, {
                x: 0,
                y: isFirst ? 32 : smileyInitialVelocityY
            })
        }

        Composite.add(engine.world, smiley)
    }

    // Create a box with rectangle(x, y, width, height)
    let boxTop = Bodies.rectangle(
        canvasWidth / 2 + canvasWallDepth * 2,
        canvasHeight + canvasWallDepth,
        canvasWidth + canvasWallDepth * 4,
        canvasWallDepth * 2,
        {
            isStatic: true,
            render: {
                visible: false
            }
        }
    )

    let boxLeft = Bodies.rectangle(
        canvasWallDepth * -1,
        canvasHeight / 2,
        canvasWallDepth * 2,
        canvasHeight,
        {
            isStatic: true,
            render: {
                visible: false
            }
        }
    )

    let boxRight = Bodies.rectangle(
        canvasWidth + canvasWallDepth,
        canvasHeight / 2,
        canvasWallDepth * 2,
        canvasHeight,
        {
            isStatic: true,
            render: {
                visible: false
            }
        }
    )

    let boxBottom = Bodies.rectangle(
        canvasWidth / 2 + canvasWallDepth * 2,
        topWallY,
        canvasWidth + canvasWallDepth * 4,
        canvasWallDepth * 2,
        {
            isStatic: true,
            render: {
                visible: false
            }
        }
    )

    // Add all of the bodies to the world
    Composite.add(engine.world, [boxTop, boxLeft, boxRight, boxBottom])
    smileyCreate(true)

    // Run the renderer
    Render.run(render)

    // Create runner
    let runner = Runner.create()

    // Run the engine
    Matter.Runner.run(runner, engine)

    // Custom Function:
    // To run the function X amount of times
    function repeatedFunction(count, maxCount) {
        if (count < maxCount) {
            smileyCreate()
            setTimeout(() => {
                repeatedFunction(count + 1, maxCount)
            }, smileySpawnDelay) // Delay in milliseconds
        }
    }
    repeatedFunction(1, smileyAmount)

    // Create mouse
    let mouse = Mouse.create(render.canvas)
    let mouseConstraint = MouseConstraint.create(engine, {
        mouse: mouse,
        constraint: {
            stiffness: 0.2,
            render: {
                visible: false
            }
        }
    })

    Composite.add(engine.world, mouseConstraint)

    // Fix: Allow native page scroll
    mouseConstraint.mouse.element.removeEventListener(
        'mousewheel',
        mouseConstraint.mouse.mousewheel
    )
    mouseConstraint.mouse.element.removeEventListener(
        'DOMMouseScroll',
        mouseConstraint.mouse.mousewheel
    )

    // Fix: Scroll on touch devices
    mouseConstraint.mouse.element.removeEventListener(
        'touchstart',
        mouseConstraint.mouse.mousedown
    )
    mouseConstraint.mouse.element.removeEventListener(
        'touchmove',
        mouseConstraint.mouse.mousemove
    )
    mouseConstraint.mouse.element.removeEventListener(
        'touchend',
        mouseConstraint.mouse.mouseup
    )

    mouseConstraint.mouse.element.addEventListener(
        'touchstart',
        mouseConstraint.mouse.mousedown,
        { passive: true }
    )
    mouseConstraint.mouse.element.addEventListener('touchmove', (e) => {
        if (mouseConstraint.body) {
            mouseConstraint.mouse.mousemove(e)
        }
    })
    mouseConstraint.mouse.element.addEventListener('touchend', (e) => {
        if (mouseConstraint.body) {
            mouseConstraint.mouse.mouseup(e)
        }
    })
}

export { initFalling2DMatterJS }
